/**
 * Kassian — live search in the header search bar.
 *
 * As the shopper types, results come from Shopify's predictive search
 * endpoint, rendered by sections/predictive-search.liquid. Requests are
 * debounced, cached per term and cancelled when outdated, so typing stays
 * smooth. Arrow keys move through results, Enter opens the highlighted one
 * (or runs a full search), Escape closes the search.
 */

const SECTION = 'predictive-search';
const DEBOUNCE = 150;

const routes = () => window.theme?.routes || {};
const strings = () => window.theme?.strings || {};
const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

class KPredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-predictive-input]');
    this.form = this.input?.form;
    this.results = this.querySelector('[data-predictive-results]');
    this.start = this.querySelector('[data-predictive-start]');
    this.status = this.querySelector('[data-predictive-status]');
    this.clearButton = this.querySelector('[data-predictive-clear]');
    if (!this.input || !this.results) return;

    this.enabled = !this.hasAttribute('data-disabled') && Boolean(routes().predictiveSearch) && 'fetch' in window;
    this.cache = new Map();
    this.index = -1;

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.clearButton?.addEventListener('click', () => {
      this.input.value = '';
      this.input.focus();
      this.onInput();
    });

    // Popular search chips fill the box and search straight away.
    this.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-predictive-term]');
      if (!chip || !this.enabled) return;
      event.preventDefault();
      this.input.value = chip.dataset.predictiveTerm;
      this.input.focus();
      this.onInput(true);
    });

    // Mouse and keyboard share one highlighted row.
    this.results.addEventListener('mousemove', (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) this.select(this.options().indexOf(option), false);
    });

    this.syncControls();
    if (this.enabled && this.term) this.onInput(true);
  }

  get term() {
    return this.input.value.trim();
  }

  /* ---------- Typing ---------- */
  onInput(immediate = false) {
    this.syncControls();
    clearTimeout(this.timer);
    const term = this.term;
    if (!this.enabled) return;
    if (!term) {
      this.abort();
      this.hideResults();
      return;
    }
    const cached = this.cache.get(term.toLowerCase());
    if (cached !== undefined) {
      this.abort();
      this.render(term, cached);
      return;
    }
    this.timer = setTimeout(() => this.load(term), immediate ? 0 : DEBOUNCE);
  }

  syncControls() {
    const hasTerm = this.term.length > 0;
    if (this.clearButton) this.clearButton.hidden = !hasTerm;
    if (this.start) this.start.hidden = hasTerm && this.enabled;
  }

  abort() {
    this.controller?.abort();
    this.removeAttribute('loading');
  }

  async load(term) {
    this.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.setAttribute('loading', '');

    const params = new URLSearchParams({
      q: term,
      section_id: SECTION,
      'resources[type]': 'product,query,collection,page,article',
      'resources[limit]': this.dataset.limit || '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
    });

    try {
      const response = await fetch(`${routes().predictiveSearch}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-content]');
      const markup = content ? content.outerHTML : '';
      this.cache.set(term.toLowerCase(), markup);
      if (term === this.term) this.render(term, markup);
    } catch (error) {
      if (error.name !== 'AbortError') this.hideResults();
    } finally {
      if (this.controller === controller) this.removeAttribute('loading');
    }
  }

  /* ---------- Results ---------- */
  render(term, markup) {
    this.results.innerHTML = markup;
    this.highlight(term);
    const hasMarkup = Boolean(markup && this.results.firstElementChild?.children.length);
    this.results.hidden = !hasMarkup;
    this.input.setAttribute('aria-expanded', String(hasMarkup));
    this.select(-1);

    const count = Number(this.results.querySelector('[data-count]')?.dataset.count || 0);
    if (this.status) {
      this.status.textContent = count > 0 ? (strings().searchCount || '').replace('[count]', count) : strings().searchNone || '';
    }
  }

  hideResults() {
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.select(-1);
  }

  /** Bold the typed words inside product and page titles. */
  highlight(term) {
    const words = term.split(/\s+/).filter((word) => word.length > 1).map(escapeRegExp);
    if (!words.length) return;
    const split = new RegExp(`(${words.join('|')})`, 'gi');
    const match = new RegExp(`^(${words.join('|')})$`, 'i');
    this.results.querySelectorAll('.predictive__product-title, .predictive__page').forEach((element) => {
      const parts = element.textContent.split(split);
      if (parts.length < 2) return;
      element.replaceChildren(
        ...parts.filter(Boolean).map((part) => {
          if (!match.test(part)) return document.createTextNode(part);
          const mark = document.createElement('mark');
          mark.textContent = part;
          return mark;
        })
      );
    });
  }

  /* ---------- Keyboard ---------- */
  options() {
    return [...this.results.querySelectorAll('[role="option"]')];
  }

  select(index, scroll = true) {
    const options = this.options();
    this.index = index;
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    const current = options[index];
    if (current) {
      this.input.setAttribute('aria-activedescendant', current.id);
      if (scroll) current.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  onKeydown(event) {
    if (this.results.hidden) return;
    const count = this.options().length;
    if (!count) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.select((this.index + 1) % count);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.select(this.index <= 0 ? count - 1 : this.index - 1);
    }
  }

  onSubmit(event) {
    const link = this.options()[this.index]?.querySelector('a');
    if (!link) return;
    event.preventDefault();
    window.location.href = link.href;
  }
}

if (!customElements.get('k-predictive-search')) customElements.define('k-predictive-search', KPredictiveSearch);
